import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, CheckCheck, Heart, Inbox, Sparkles, ShieldCheck } from "lucide-react";

const relTime = (ts) => {
  if (!ts) return "";
  const d = (Date.now() - new Date(ts).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  try { return new Date(ts).toLocaleDateString(); } catch { return ""; }
};

const meta = (type = "") => {
  const t = type.toLowerCase();
  if (t.includes("match")) return { icon: Heart, tint: "bg-emerald-100 text-emerald-700", route: "/matches" };
  if (t.includes("interest") || t.includes("received")) return { icon: Inbox, tint: "bg-amber-100 text-amber-700", route: "/matches" };
  if (t.includes("verif")) return { icon: ShieldCheck, tint: "bg-blue-100 text-blue-700", route: "/profile" };
  return { icon: Sparkles, tint: "bg-mint text-emerald-700", route: "/discovery" };
};

export default function Notifications() {
  const [notes, setNotes] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setNotes(data.notifications || []); }
    catch { setNotes([]); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async () => { try { await api.post("/notifications/read", {}); } catch {} load(); };

  const target = (n) => n.actionData?.userId || n.actionData?.targetId || n.actionData?.profileId;
  const onClick = (n) => {
    const t = target(n);
    if (t) navigate(`/profiles/${t}`);
    else navigate(meta(n.type).route);
  };

  const unread = (notes || []).filter((n) => !n.read).length;

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-amber-300 shadow-sm">
            <Bell className="h-6 w-6" />
            {unread > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white">{unread}</span>}
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">Notifications</h1>
            <p className="text-sm text-slate-500">{unread > 0 ? `${unread} new update${unread > 1 ? "s" : ""}` : "You're all caught up"}</p>
          </div>
        </div>
        {unread > 0 && <button onClick={markRead} data-testid="mark-read-btn" className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm ring-1 ring-emerald-900/10 hover:bg-mint"><CheckCheck className="h-4 w-4" /> Mark all read</button>}
      </div>

      {notes === null ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-emerald-300 bg-white py-20 text-center" data-testid="notifications-empty">
          <Bell className="mb-3 h-10 w-10 text-emerald-400" />
          <h3 className="font-serif text-2xl font-semibold text-emerald-900">No notifications yet</h3>
          <p className="mt-2 max-w-sm text-slate-500">New matches and interests will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notifications-list">
          {notes.map((n, i) => {
            const m = meta(n.type);
            const Icon = m.icon;
            return (
              <button key={n.id || i} onClick={() => onClick(n)} data-testid={`notification-${i}`}
                className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md animate-fade-up ${n.read ? "border-emerald-900/10 bg-white" : "border-amber-300/60 bg-amber-50/60"}`}
                style={{ animationDelay: `${i * 50}ms` }}>
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${m.tint}`}><Icon className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-emerald-950">{n.title}</p>
                  {n.body && <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{n.body}</p>}
                  {n.ts && <p className="mt-1 text-xs text-slate-400">{relTime(n.ts)}</p>}
                </div>
                {!n.read && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-rose-500" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
